import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Loader2, Plus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useUserTags, getTagColorClass, UserTag } from "@/hooks/useUserTags";
import { cn } from "@/lib/utils";

interface UserMembership {
  credits_balance: number;
  is_unlimited: boolean;
  unlimited_expires_at: string | null;
  unlimited_starts_at: string | null;
  start_date: string | null;
  next_renewal_date: string | null;
}

interface UserRole {
  role: string;
}

interface UserTagInfo {
  id: string;
  name: string;
  color: string;
}

export interface EditableUser {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  member_type: string | null;
  user_memberships: UserMembership | UserMembership[] | null;
  user_roles: UserRole[] | null;
  tags?: UserTagInfo[];
}

interface EditUserDialogProps {
  user: EditableUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (userId: string, data: {
    first_name: string;
    last_name: string;
    phone: string;
    member_type: string;
    credits_balance: number;
    is_unlimited: boolean;
    unlimited_expires_at: string | null;
    is_admin: boolean;
  }) => Promise<void>;
  isSaving: boolean;
}

const getMembership = (user: EditableUser): UserMembership | undefined => {
  if (!user.user_memberships) return undefined;
  if (Array.isArray(user.user_memberships)) return user.user_memberships[0];
  return user.user_memberships;
};

export const EditUserDialog = ({
  user,
  open,
  onOpenChange,
  onSave,
  isSaving,
}: EditUserDialogProps) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [memberType, setMemberType] = useState("member");
  const [creditsBalance, setCreditsBalance] = useState("");
  const [isUnlimited, setIsUnlimited] = useState(false);
  const [unlimitedExpiresAt, setUnlimitedExpiresAt] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedTags, setSelectedTags] = useState<UserTagInfo[]>([]);
  const [tagPopoverOpen, setTagPopoverOpen] = useState(false);
  
  const { allTags, manageUserTags, isManagingTags } = useUserTags();

  // Populate form when user changes
  useEffect(() => {
    if (user) {
      const membership = getMembership(user);
      setFirstName(user.first_name || "");
      setLastName(user.last_name || "");
      setPhone(user.phone || "");
      setMemberType(user.member_type || "member");
      setCreditsBalance(String(membership?.credits_balance ?? 0));
      setIsUnlimited(membership?.is_unlimited || false);
      setUnlimitedExpiresAt(
        membership?.unlimited_expires_at
          ? format(new Date(membership.unlimited_expires_at), "yyyy-MM-dd")
          : ""
      );
      setIsAdmin(user.user_roles?.some((r) => r.role === "admin") || false);
      setSelectedTags(user.tags || []);
    }
  }, [user]);

  const handleAddTag = (tag: UserTag) => {
    if (!selectedTags.find((t) => t.id === tag.id)) {
      setSelectedTags([...selectedTags, { id: tag.id, name: tag.name, color: tag.color }]);
    }
    setTagPopoverOpen(false);
  };

  const handleRemoveTag = (tagId: string) => {
    setSelectedTags(selectedTags.filter((t) => t.id !== tagId));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    // Determine tag changes
    const originalTagIds = user.tags?.map((t) => t.id) || [];
    const newTagIds = selectedTags.map((t) => t.id);
    const tagsToAdd = newTagIds.filter((id) => !originalTagIds.includes(id));
    const tagsToRemove = originalTagIds.filter((id) => !newTagIds.includes(id));

    // Update tags if changed
    if (tagsToAdd.length > 0 || tagsToRemove.length > 0) {
      await manageUserTags({
        userId: user.id,
        tagsToAdd,
        tagsToRemove,
      });
    }

    await onSave(user.id, {
      first_name: firstName,
      last_name: lastName,
      phone,
      member_type: memberType,
      credits_balance: parseInt(creditsBalance) || 0,
      is_unlimited: isUnlimited,
      unlimited_expires_at: isUnlimited && unlimitedExpiresAt 
        ? new Date(unlimitedExpiresAt).toISOString() 
        : null,
      is_admin: isAdmin,
    });
  };

  const availableTags = allTags.filter(
    (t) => t.tag_type !== 'dynamic' && !t.is_system && !selectedTags.find((st) => st.id === t.id)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="memberType">Member Type</Label>
            <Select value={memberType} onValueChange={setMemberType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="host">Host</SelectItem>
                <SelectItem value="artist">Artist</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="credits">Credits Balance</Label>
            <Input
              id="credits"
              type="number"
              min={0}
              value={creditsBalance}
              onChange={(e) => setCreditsBalance(e.target.value)}
            />
          </div>

          <div className="space-y-3 rounded-md border p-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="unlimited">Unlimited Access</Label>
              <Switch
                id="unlimited"
                checked={isUnlimited}
                onCheckedChange={setIsUnlimited}
              />
            </div>
            {isUnlimited && (
              <div className="space-y-2">
                <Label htmlFor="unlimitedExpires">Expires At</Label>
                <Input
                  id="unlimitedExpires"
                  type="date"
                  value={unlimitedExpiresAt}
                  onChange={(e) => setUnlimitedExpiresAt(e.target.value)}
                />
              </div>
            )}
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <Label htmlFor="admin">Admin Role</Label>
            <Switch
              id="admin"
              checked={isAdmin}
              onCheckedChange={setIsAdmin}
            />
          </div>

          {/* Tags Section */}
          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-2 p-3 border rounded-md min-h-[60px]">
              {selectedTags.map((tag) => (
                <Badge 
                  key={tag.id} 
                  variant="outline"
                  className={cn("flex items-center gap-1", getTagColorClass(tag.color))}
                >
                  {tag.name}
                  <button 
                    type="button"
                    onClick={() => handleRemoveTag(tag.id)} 
                    className="hover:opacity-70"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <Popover open={tagPopoverOpen} onOpenChange={setTagPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm" 
                    className="h-6 px-2"
                    disabled={availableTags.length === 0}
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-2" align="start">
                  <ScrollArea className="max-h-48">
                    <div className="flex flex-col gap-1">
                      {availableTags.map((tag) => (
                        <Button
                          key={tag.id}
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="justify-start"
                          onClick={() => handleAddTag(tag)}
                        >
                          <Badge 
                            variant="outline" 
                            className={cn("text-xs", getTagColorClass(tag.color))}
                          >
                            {tag.name}
                          </Badge>
                        </Button>
                      ))}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSaving || isManagingTags}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || isManagingTags}>
              {(isSaving || isManagingTags) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};